import {
  onScopeDispose,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue';
import { SEARCH_DEBOUNCE_MS, SEARCH_SETTLE_MARGIN_MS } from '~/utils/search';

/**
 * Tracks the window between a keystroke and the request that keystroke will
 * eventually cause.
 *
 * Every search box here debounces before it reaches the API, so for that window
 * nothing is `pending` yet while the results on screen still answer the
 * PREVIOUS query. A page that only checks `pending` will happily announce "No
 * Results Found" about a query the user is halfway through typing.
 *
 * Returns a flag that is true from the first keystroke until the debounced
 * request has actually started, so pages can hold back their empty states
 * without having to duplicate the timing.
 */
export function useQuerySettling(query: MaybeRefOrGetter<string>) {
  const settling = ref(false);
  let timer: ReturnType<typeof setTimeout> | undefined;

  watch(
    () => toValue(query),
    () => {
      settling.value = true;
      clearTimeout(timer);
      // Slightly longer than the debounce itself so the request has certainly
      // been made — and `pending` flipped — before this clears.
      timer = setTimeout(() => {
        settling.value = false;
      }, SEARCH_DEBOUNCE_MS + SEARCH_SETTLE_MARGIN_MS);
    },
  );

  onScopeDispose(() => clearTimeout(timer));

  return settling;
}
